import { handleExpiredTokens } from 'api/apiReqSagaCreator/httpRequest';
import { ProcessInstanceDetails } from 'api/endpoints/processInstance';
import { OdataURLBuilder } from 'api/types/OdataURLBuilder';
import {
  ApiError,
  ApiResponseAsync,
  axiosResponseToApiResponse,
  translateAxiosErrorToApiError,
} from 'container/models';

import BaseRepo from '../../BaseRepo';
import { buildBadRequestApiError } from '../../helpers/errors';
import {
  FinishIndexDocumentResponse,
  IIndexingDocumentsRepo,
  IndexDocumentWithHyperMedia,
  LockDocumentResponse,
} from './IIndexingDocumentsRepo';

export class IndexingDocumentRepo
  extends BaseRepo
  implements IIndexingDocumentsRepo
{
  deleteDocWithInstance = (
    document: IndexDocumentWithHyperMedia
  ): ApiResponseAsync<void> => {
    const link = document._links?.['deleteWithProcessInstance'];
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "deleteWithProcessInstance" is not available in document'
          )
        )
      );
    }

    return this.helpers.handlePost(link.href, document);
  };

  getProcessInstance = (link: Link): ApiResponseAsync<ProcessInstanceDetails> =>
    this.helpers.handleGet(link.href, {
      responseStopTransformPaths: ['data_model.variables'],
    });

  upsert = (
    link: Link,
    name: string,
    draftProcessId: string,
    processInstanceId: string,
    indexingSourceId: string,
    fileFieldId: string
  ): ApiResponseAsync<IndexDocumentWithHyperMedia> =>
    handleExpiredTokens(() =>
      this.client.post<IndexDocumentWithHyperMedia>(
        new OdataURLBuilder(link, { process_id: draftProcessId }).toString(),
        {
          name,
          processInstanceId,
          indexingSourceId,
          fileFieldId,
        }
      )
    )
      .then((response) => response.data)
      .catch(translateAxiosErrorToApiError);

  lock = (
    link: Link,
    scanConfigurationId: string,
    indexingDocumentId: string
  ): ApiResponseAsync<LockDocumentResponse> =>
    handleExpiredTokens(() =>
      this.client.post<LockDocumentResponse>(
        new OdataURLBuilder(link, {
          index_document_id: indexingDocumentId,
        }).toString(),
        {
          scanConfigurationId,
        }
      )
    )
      .then((response) => response.data)
      .catch(translateAxiosErrorToApiError);

  lockNext = (link: Link): ApiResponseAsync<Nullable<LockDocumentResponse>> =>
    handleExpiredTokens(() =>
      this.client.post<Nullable<LockDocumentResponse>>(link.href, {})
    )
      .then((response) => (response.status === 204 ? null : response.data))
      .catch(translateAxiosErrorToApiError);

  unLock = (link: Link): ApiResponseAsync<IndexDocumentWithHyperMedia> =>
    axiosResponseToApiResponse(
      handleExpiredTokens(() =>
        this.client.post<IndexDocumentWithHyperMedia>(link.href, {})
      )
    );

  finish = (link: Link): ApiResponseAsync<FinishIndexDocumentResponse> =>
    handleExpiredTokens(() =>
      this.client.post<FinishIndexDocumentResponse>(link.href, {})
    )
      .then((response) => response.data)
      .catch(translateAxiosErrorToApiError);

  deleteDoc = (
    document: IndexDocumentWithHyperMedia
  ): ApiResponseAsync<null> => {
    const link = document._links?.delete;

    if (!link)
      return Promise.resolve<ApiError>({
        status: 400,
        statusText: 'Bad Request',
        data: [
          {
            code: -1,
            description: 'No delete link found',
          },
        ],
      });

    return handleExpiredTokens(() =>
      this.client.delete(new OdataURLBuilder(link).toString())
    )
      .then(() => null)
      .catch(translateAxiosErrorToApiError);
  };

  getDocumentCount = (
    link: Link,
    indexingSourceId: string
  ): ApiResponseAsync<number> => {
    const query = new OdataURLBuilder(link);
    query.setFilter(
      `indexing_source_id eq '${indexingSourceId}' AND lock_user eq null`
    );
    query.setCount(true);

    return axiosResponseToApiResponse(
      handleExpiredTokens(() => this.client.get<number>(query.toString()))
    );
  };
}
